import { inflate } from 'pako'
import type { TicketPosition } from '@/Overtime/types/markets'
import { formatMarketOdds } from '@/Overtime/utils/markets'
import { OddsType } from '@/Overtime/enums/markets'

// Helper function to convert decompressed data to TicketPosition format
const convertToTicketPosition = (data: any): TicketPosition => {
  // Extract odds data from the market object
  const marketOdds = data.market?.odds || []

  // For the selected position's odd, we need to get it from the market odds array
  // or fall back to totalOdds or a default value
  let selectedOdd = 0
  if (
    marketOdds.length > 0 &&
    data.position >= 0 &&
    data.position < marketOdds.length
  ) {
    selectedOdd = marketOdds[data.position]
  } else if (data.market?.odd) {
    selectedOdd = data.market.odd
  } else if (data.totalOdds) {
    selectedOdd = data.totalOdds
  }

  // If we still don't have a valid selectedOdd, create a fallback odds array
  let finalOdds = marketOdds
  if ((!selectedOdd || selectedOdd === 0) && data.totalOdds) {
    // Create a simple two-position odds array with the totalOdds in the correct position
    finalOdds = new Array(2).fill(1.5) // Default odds
    if (data.position >= 0 && data.position < finalOdds.length) {
      finalOdds[data.position] = data.totalOdds
    }
    selectedOdd = data.totalOdds
  }

  // Format the selected odd using the same formatting as Discord
  const formattedSelectedOdd = selectedOdd
    ? parseFloat(formatMarketOdds(OddsType.DECIMAL, selectedOdd))
    : 0

  // Format all odds in the array
  const formattedOdds = finalOdds.map(odd =>
    parseFloat(formatMarketOdds(OddsType.DECIMAL, odd))
  )

  return {
    chainId: data.chainId || 10,
    gameId: data.gameId || '',
    leagueId: data.leagueId || 0,
    typeId: data.typeId || 0,
    playerId: data.playerId || 0,
    playerName: data.playerName || '',
    line: data.line || data.market?.line || 0,
    position: data.position || 0,
    combinedPositions: data.market?.combinedPositions || [],
    isOneSideMarket: data.market?.isOneSideMarket || false,
    isPlayerPropsMarket: data.market?.isPlayerPropsMarket || false,
    homeTeam: data.homeTeam || '',
    awayTeam: data.awayTeam || '',
    playerProps: {
      playerId: data.playerId || 0,
      playerName: data.playerName || '',
    },
    live: data.live || false,
    market: {
      typeId: data.typeId || 0,
      line: data.line || data.market?.line || 0,
      status: data.market?.status || 0,
      odds: formattedOdds, // Array of all odds (formatted)
      odd: formattedSelectedOdd, // Selected position's odd (formatted)
      positionNames: data.market?.positionNames || [],
      gameId: data.gameId || '',
      sport: data.market?.sport || '',
      leagueId: data.leagueId || 0,
      leagueName: data.market?.leagueName || '',
      subLeagueId: data.market?.subLeagueId || 0,
      type: data.market?.type || '',
      maturity: data.market?.maturity || 0,
      maturityDate: data.market?.maturityDate
        ? new Date(data.market.maturityDate)
        : new Date(),
      homeTeam: data.homeTeam || '',
      awayTeam: data.awayTeam || '',
      isOpen: data.market?.isOpen !== undefined ? data.market.isOpen : true,
      isResolved: data.market?.isResolved || false,
      isCancelled: data.market?.isCancelled || false,
      isPaused: data.market?.isPaused || false,
      isOneSideMarket: data.market?.isOneSideMarket || false,
      isPlayerPropsMarket: data.market?.isPlayerPropsMarket || false,
      isOneSidePlayerPropsMarket:
        data.market?.isOneSidePlayerPropsMarket || false,
      isYesNoPlayerPropsMarket: data.market?.isYesNoPlayerPropsMarket || false,
      playerProps: {
        playerId: data.playerId || 0,
        playerName: data.playerName || '',
      },
      combinedPositions: data.market?.combinedPositions || [],
      marketName: data.market?.marketName || '',
      displayPosition: data.position || 0,
      tournamentName: data.market?.tournamentName || '',
    },
  }
}

const decompressOvertimeData = (encoded: string) => {
  try {
    // Convert from base64
    const compressed = Buffer.from(
      encoded.replace(/-/g, '+').replace(/_/g, '/'),
      'base64'
    )
    // Convert Buffer to Uint8Array and decompress
    const uint8Array = new Uint8Array(compressed)
    const decompressedData = inflate(uint8Array, { to: 'string' })
    const data = JSON.parse(decompressedData)

    // Convert back to original format
    if (Array.isArray(data)) {
      return data.map(bet =>
        convertToTicketPosition({
          gameId: bet.g, // gameId
          leagueId: bet.l, // leagueId
          typeId: bet.t, // typeId
          position: bet.p, // position
          homeTeam: bet.h, // homeTeam
          awayTeam: bet.a, // awayTeam
          chainId: bet.c, // chainId
          market: bet.m, // market (contains odds and other market data)
          betAmount: bet.ba, // betAmount
          totalOdds: bet.to, // totalOdds
          betToken: bet.bt, // betToken
          live: bet.li, // live
          selectedCollateralSymbol: bet.sc, // selectedCollateralSymbol
          line: bet.ln, // line
          playerId: bet.pi, // playerId
          playerName: bet.pn, // playerName
        })
      )
    } else {
      return convertToTicketPosition({
        gameId: data.g, // gameId
        leagueId: data.l, // leagueId
        typeId: data.t, // typeId
        position: data.p, // position
        homeTeam: data.h, // homeTeam
        awayTeam: data.a, // awayTeam
        chainId: data.c, // chainId
        market: data.m, // market (contains odds and other market data)
        betAmount: data.ba, // betAmount
        totalOdds: data.to, // totalOdds
        betToken: data.bt, // betToken
        live: data.li, // live
        selectedCollateralSymbol: data.sc, // selectedCollateralSymbol
        line: data.ln, // line
        playerId: data.pi, // playerId
        playerName: data.pn, // playerName
      })
    }
  } catch (error) {
    console.error('Overtime decompression error:', error)
    throw error
  }
}

export default decompressOvertimeData
